"use strict";
/**
 * This module includes the Operator enum, Operator mapping objects and a set of related functions
 * such as range, date and text operator calculation according to the filter values.
 * usage:
 * `import { } from 'priority_utils/lib/operators'`
 *
 *
 * @module operators
 * @preferred
 *
 */
var _a;
Object.defineProperty(exports, "__esModule", { value: true });
exports.getTextString = exports.getTextQuery = exports.getTextOperator = exports.getDateOperator = exports.getRangeOperator = exports.DateOperatorMapping = exports.RangeOperatorMapping = exports.OperatorMapping = exports.ProcOperator = exports.Operator = void 0;
/**
 * Enum of operator types as presented to the user
 */
var Operator;
(function (Operator) {
    Operator[Operator["Contains"] = 0] = "Contains";
    Operator[Operator["StartsWith"] = 1] = "StartsWith";
    Operator[Operator["EndsWith"] = 2] = "EndsWith";
    Operator[Operator["Between"] = 3] = "Between";
    Operator[Operator["Equals"] = 4] = "Equals";
    Operator[Operator["NotEquals"] = 5] = "NotEquals";
    Operator[Operator["Less"] = 6] = "Less";
    Operator[Operator["LessOrEqual"] = 7] = "LessOrEqual";
    Operator[Operator["Greater"] = 8] = "Greater";
    Operator[Operator["GreaterOrEqual"] = 9] = "GreaterOrEqual";
    Operator[Operator["Later"] = 10] = "Later";
    Operator[Operator["Before"] = 11] = "Before";
    Operator[Operator["Empty"] = 12] = "Empty";
    Operator[Operator["NotEmpty"] = 13] = "NotEmpty";
})(Operator = exports.Operator || (exports.Operator = {}));
/* the order of the operators shouldn't be changed
because reflects the operators ids coming from the server
**/
var ProcOperator;
(function (ProcOperator) {
    ProcOperator[ProcOperator["Equals"] = 0] = "Equals";
    ProcOperator[ProcOperator["Less"] = 1] = "Less";
    ProcOperator[ProcOperator["LessOrEqual"] = 2] = "LessOrEqual";
    ProcOperator[ProcOperator["Greater"] = 3] = "Greater";
    ProcOperator[ProcOperator["GreaterOrEqual"] = 4] = "GreaterOrEqual";
    ProcOperator[ProcOperator["NotEquals"] = 5] = "NotEquals";
    ProcOperator[ProcOperator["Between"] = 6] = "Between";
    ProcOperator[ProcOperator["AndNot"] = 7] = "AndNot";
    ProcOperator[ProcOperator["Or"] = 8] = "Or";
})(ProcOperator = exports.ProcOperator || (exports.ProcOperator = {}));
/**
 * Mapping of the Operator Enum to the matching arithmetic operator
 */
exports.OperatorMapping = (_a = {},
    _a[Operator.Contains] = '=',
    _a[Operator.StartsWith] = '=',
    _a[Operator.EndsWith] = '=',
    _a[Operator.Equals] = '=',
    _a[Operator.NotEquals] = '<>',
    _a[Operator.Less] = '<',
    _a[Operator.LessOrEqual] = '<=',
    _a[Operator.Before] = '<=',
    _a[Operator.Greater] = '>',
    _a[Operator.GreaterOrEqual] = '>=',
    _a[Operator.Later] = '>=',
    _a[Operator.Between] = '',
    _a[Operator.Empty] = '=',
    _a[Operator.NotEmpty] = '<>',
    _a);
/**
 * Mapping of arithmetic operator to Operator Enum with meaning of number range.
 */
exports.RangeOperatorMapping = {
    '<': Operator.Less,
    '<=': Operator.LessOrEqual,
    '<>': Operator.NotEquals,
    '=': Operator.Equals,
    '>': Operator.Greater,
    '>=': Operator.GreaterOrEqual,
};
/**
 * Mapping of arithmetic operator to Operator Enum with meaning of date/time range.
 */
exports.DateOperatorMapping = {
    '<': Operator.Before,
    '<=': Operator.Before,
    '<>': Operator.NotEquals,
    '=': Operator.Equals,
    '>': Operator.Later,
    '>=': Operator.Later,
};
/**
 * getRangeOperator takes min value and max value and returns the matching Operator according to the existance of each value.
 * when min and max values exist and are equal - the Operator is Equal
 * when min and max values exist and are not equal - the Operator is Between
 * when only min value exists - the Operator is GraterOrEqual
 * when only max value exists - the Operator is LessOrEqual
 * @param min minimum value
 * @param max maximim value
 */
function getRangeOperator(min, max) {
    if (min === void 0) { min = ''; }
    if (max === void 0) { max = ''; }
    if (min !== '' && max !== '') {
        if (min === max) {
            return Operator.Equals;
        }
        return Operator.Between;
    }
    if (min !== '') {
        return Operator.GreaterOrEqual;
    }
    if (max !== '') {
        return Operator.LessOrEqual;
    }
    return;
}
exports.getRangeOperator = getRangeOperator;
/**
 * getDateOperator takes from value and to value and returns the matching Operator according to the existance of each value.
 * when from and to values exist and are equal - the Operator is Equal
 * when from and to values exist and are not equal - the Operator is Between
 * when only from value exists - the Operator is Later
 * when only to value exists - the Operator is Brfore
 * @param from from value
 * @param to to value
 */
function getDateOperator(from, to) {
    if (from && to) {
        if (from === to) {
            return Operator.Equals;
        }
        return Operator.Between;
    }
    if (from) {
        return Operator.Later;
    }
    if (to) {
        return Operator.Before;
    }
    return;
}
exports.getDateOperator = getDateOperator;
/**
 * getTextOperator takes the string value and returns the matching Operator according to wild chars.
 * when there is a '*' at begining and end - the Operator is Contains
 * when there is a '*' only at begining - the Operator is EndsWith
 * when there is a '*' only at the end - the Operator is StartsWith
 * when there are no '*' at the beginning or end - the Oprator is Equals
 * @param value string value
 */
function getTextOperator(value) {
    if (value === void 0) { value = ''; }
    if (value) {
        var startsWith = value.startsWith('*');
        var endsWith = value.endsWith('*');
        if (startsWith && endsWith) {
            return Operator.Contains;
        }
        if (startsWith) {
            return Operator.EndsWith;
        }
        if (endsWith) {
            return Operator.StartsWith;
        }
        return Operator.Equals;
    }
}
exports.getTextOperator = getTextOperator;
/**
 * getTextQuery takes a string value and Operator adds '*' wild char according to the Operator.
 * for Operator Contains - adds a '*' at begining and end.
 * for Operator EndsWith - adds a '*' only at begining.
 * for Operator StartsWith - adds a '*' only at the end.
 * @param value string value
 */
function getTextQuery(operator, value) {
    if (value === void 0) { value = ''; }
    if (value) {
        switch (operator) {
            case Operator.StartsWith:
                return "".concat(value, "*");
            case Operator.EndsWith:
                return "*".concat(value);
            case Operator.Contains:
                return "*".concat(value, "*");
            default:
                return value;
        }
    }
    return value;
}
exports.getTextQuery = getTextQuery;
/**
 * getTextString takes a string value and removes the '*' wild char at the beginning and the end.
 * @param value string value
 */
function getTextString(value) {
    if (value === void 0) { value = ''; }
    // return '*' for string that has only '*'
    if (/^\*+$/.test(value)) {
        return '*';
    }
    return value.replace(/^\*+/, '').replace(/\*+$/, '');
}
exports.getTextString = getTextString;
